import {useCategory} from '@/hooks/category/useCategory'
import {ICategoryEnrichmentEntry, LayoutType} from 'hec/types/common'
import {SfccCategoryEntry, SfccCategory, NormalizedCategory} from './types'

export type {SfccCategoryEntry, NormalizedCategory} from './types'

const isParentCategory = (category: SfccCategory | undefined): boolean => {
    if (!category) return false
    return (
        (category.onlineSubCategoriesCount ?? 0) > 0 ||
        (Array.isArray(category.categories) && category.categories.length > 0)
    )
}
// IF category isnt a parent it looks at the parentCat tree , excludes 'all' categories to fetch subcategories
const getFallbackParentId = (category: SfccCategory | undefined): string | null => {
    return (
        category?.parentCategoryTree?.find(
            (entry) => !entry.id.includes('all') && entry.id !== category.id
        )?.id ?? null
    )
}

export const useSubcategories = (categoryId: string | undefined): SfccCategoryEntry[] => {
    const {data: sfccCategory} = useCategory(
        {parameters: {id: categoryId, levels: 2}},
        {enabled: !!categoryId}
    )

    const category = sfccCategory as SfccCategory | undefined
    const isParent = isParentCategory(category)
    const fallbackParentId = !isParent ? getFallbackParentId(category) : null

    const {data: fallbackCategory} = useCategory(
        {parameters: {id: fallbackParentId, levels: 2}},
        {enabled: !!fallbackParentId}
    )

    if (!categoryId) return []

    if (isParent) {
        return (category?.categories ?? []).map((cat) => ({
            id: cat.id,
            name: cat.name ?? ''
        }))
    }

    return (fallbackCategory?.categories ?? []).map((cat) => ({
        id: cat.id,
        name: cat.name ?? ''
    }))
}
//Read this plz
// Merges SFCC categories with Builder enrichment entries.
// SFCC categories are the base — all are shown. Builder entries act as overrides,
// adding icons, custom names, or excluding specific categories.
export const mergeWithBuilderEnrichment = (
    sfccCategories: SfccCategoryEntry[],
    builderEnrichmentData: ICategoryEnrichmentEntry[]
): NormalizedCategory[] => {
    const builderByShortName = Array.isArray(builderEnrichmentData)
        ? builderEnrichmentData.reduce((acc: Record<string, ICategoryEnrichmentEntry>, entry) => {
              const shortName = entry?.data?.categoryShortName
              if (typeof shortName === 'string') acc[shortName] = entry
              return acc
          }, {} as Record<string, ICategoryEnrichmentEntry>)
        : {}

    return sfccCategories
        .map((cat) => {
            const builderEntry = builderByShortName[cat.id]
            return {
                id: cat.id,
                name: builderEntry?.name ?? cat.name,
                categoryShortName: cat.id,
                categoryIcon: builderEntry?.data?.categoryIcon,
                exclude: builderEntry?.data?.excludeFromCategoryIconsBanner === true,
                iconRank: builderEntry?.data?.iconRank
            }
        })
        .filter((cat) => !cat.exclude)
        .sort((a, b) => {
            const aHasRank = typeof a.iconRank === 'number'
            const bHasRank = typeof b.iconRank === 'number'
            if (aHasRank && bHasRank) return (a.iconRank ?? 0) - (b.iconRank ?? 0)
            if (aHasRank) return -1
            if (bHasRank) return 1
            return 0
        })
}

export const applyFilter = (
    searchParams: URLSearchParams,
    filter: Record<string, string>
): URLSearchParams => {
    if (!(searchParams instanceof URLSearchParams)) {
        throw new Error('searchParams must be an instance of URLSearchParams')
    }
    if (!filter || typeof filter !== 'object') {
        return searchParams
    }
    const params = new URLSearchParams(searchParams.toString())
    if (filter?.['Maximum Price']) {
        params.set('pmax', filter['Maximum Price'])
    }
    if (filter?.['Minimum Price']) {
        params.set('pmin', filter['Minimum Price'])
    }
    if (filter?.['Search parameters']) {
        new URLSearchParams(filter['Search parameters']).forEach((value, key) => {
            params.set(key, value)
        })
    }
    return params
}

export const determineLayoutClasses = (layoutType?: LayoutType): string => {
    switch (layoutType) {
        case 'scroll':
            return 'flex flex-row gap-[10px] justify-start items-start overflow-auto max-w-[350px] sm:container md:flex-nowrap sm:items-center sm:justify-center'
        case 'wrap':
            return 'flex flex-wrap gap-[10px] justify-center items-center w-full max-w-screen'
        case 'grid':
            return 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-[10px] justify-center items-center w-full max-w-screen'
        default:
            return 'flex flex-row gap-[10px] justify-center items-center w-full max-w-screen'
    }
}
